import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactElement, type SyntheticEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ConnectDraft,
  type KeptFields,
  MAX_ACCOUNT_NUMBER_LENGTH,
  MAX_FIELD_LENGTH,
  toConnectDraft,
} from './trading-account-view';

/** Button type that submits the form; a constant because the i18n lint rule flags string attributes. */
const SUBMIT = 'submit';

/** Props of {@link AccountForm}. */
export interface AccountFormProps {
  /**
   * What the fields hold when the form first appears: nothing on a new connection (I7, R2), the
   * server of the account on a reconnection (R16). The number is never prefilled.
   */
  readonly initial: KeptFields;
  /** The last 4 digits of the account to reconnect, shown as a hint under the number (R16, Q81). */
  readonly last4Hint: string | null;
  /** Which text the button carries (R9, R16). */
  readonly submitLabel: 'check' | 'checkAgain' | 'reconnect';
  /** True while a verification runs or a request is in flight: fields and button are disabled. */
  readonly disabled: boolean;
  /** The valid draft, passed once; the password field is emptied right after (R4). */
  readonly onSubmit: (draft: ConnectDraft) => void;
}

/**
 * The connection form of E4 (specs/S6-trading-account/spec.md R2, R3, R4, R16, R31): the platform
 * as fixed text (MetaTrader 5 only, ADR 0008), three fields with their help linked by
 * `aria-describedby`, and the button, disabled until the contract accepts the draft.
 *
 * **I9.** The password lives in this component's state and nowhere else. It is emptied in the same
 * handler that hands the draft over, whatever happens next: `type="password"` and
 * `autoComplete="off"` so no browser offers to save it. The number and the server
 * stay in the state, so a refused submit keeps them (R28).
 */
export function AccountForm({
  initial,
  last4Hint,
  submitLabel,
  disabled,
  onSubmit,
}: AccountFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = { number: useId(), password: useId(), server: useId() };
  const [accountNumber, setAccountNumber] = useState(initial.accountNumber);
  const [password, setPassword] = useState('');
  const [server, setServer] = useState(initial.server);
  const valid = toConnectDraft({ accountNumber, password, server }) !== null;

  function submit(event: SyntheticEvent): void {
    event.preventDefault();
    const draft = toConnectDraft({ accountNumber, password, server });
    if (disabled || draft === null) return;
    // R4, I9: the password leaves the field the moment it is handed over, success or not.
    setPassword('');
    onSubmit(draft);
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={submit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t('onboarding.tradingAccount.platformLabel')}
        </Typography>
        <Typography sx={{ fontWeight: 600, minHeight: 44, display: 'flex', alignItems: 'center' }}>
          {t('onboarding.tradingAccount.platformValue')}
        </Typography>
      </Box>
      <TextField
        id={ids.number}
        label={t('onboarding.tradingAccount.accountNumber.label')}
        helperText={
          <>
            {last4Hint === null ? null : (
              <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                {t('onboarding.tradingAccount.unreachable.hint', { last4: last4Hint })}
              </Box>
            )}
            {t('onboarding.tradingAccount.accountNumber.help')}
          </>
        }
        value={accountNumber}
        onChange={(event) => {
          // Digits only: the field never holds what the contract refuses (R2).
          setAccountNumber(event.target.value.replaceAll(/\D/g, ''));
        }}
        disabled={disabled}
        fullWidth
        slotProps={{
          htmlInput: {
            inputMode: 'numeric',
            autoComplete: 'off',
            maxLength: MAX_ACCOUNT_NUMBER_LENGTH,
          },
        }}
      />
      <TextField
        id={ids.password}
        label={t('onboarding.tradingAccount.password.label')}
        helperText={t('onboarding.tradingAccount.password.help')}
        type="password"
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
        disabled={disabled}
        fullWidth
        slotProps={{ htmlInput: { autoComplete: 'off', maxLength: MAX_FIELD_LENGTH } }}
      />
      <TextField
        id={ids.server}
        label={t('onboarding.tradingAccount.server.label')}
        helperText={t('onboarding.tradingAccount.server.help')}
        value={server}
        onChange={(event) => {
          setServer(event.target.value);
        }}
        disabled={disabled}
        fullWidth
        slotProps={{
          htmlInput: { autoComplete: 'off', autoCapitalize: 'off', maxLength: MAX_FIELD_LENGTH },
        }}
      />
      <Button
        type={SUBMIT}
        variant="contained"
        size="large"
        fullWidth
        disabled={disabled || !valid}
        sx={{ minHeight: 52 }}
      >
        {t(`onboarding.tradingAccount.${submitLabel}`)}
      </Button>
    </Box>
  );
}
